import { useCallback, useEffect, useRef, useState } from 'react';
import { organizationApi } from './api';
import type { OrganizationWorkspace, OrganizationVersion, StructureApplyResult } from './model';

export function useOrganizationWorkspace(companyId: string) {
  const [workspace, setWorkspace] = useState<OrganizationWorkspace | null>(null);
  const [versions, setVersions] = useState<OrganizationVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyError, setHistoryError] = useState('');
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const generation = useRef(0);
  const companyScope = useRef(companyId);
  companyScope.current = companyId;

  const refresh = useCallback(async () => {
    const requestGeneration = ++generation.current;
    setError('');
    setHistoryError('');
    const [current, history] = await Promise.allSettled([
      organizationApi.workspace(companyId),
      organizationApi.versions(companyId),
    ]);
    if (requestGeneration !== generation.current) return;
    if (current.status === 'fulfilled') setWorkspace(current.value);
    else
      setError(
        current.reason instanceof Error
          ? current.reason.message
          : 'The organization could not be loaded.'
      );
    if (history.status === 'fulfilled') {
      setVersions(history.value.versions);
      setNextCursor(history.value.nextCursor);
    } else
      setHistoryError(
        history.reason instanceof Error
          ? history.reason.message
          : 'Version history could not be loaded.'
      );
    setLoading(false);
  }, [companyId]);

  useEffect(() => {
    setWorkspace(null);
    setVersions([]);
    setNextCursor(null);
    setLoadingMore(false);
    setLoading(true);
    void refresh();
    return () => {
      generation.current++;
    };
  }, [refresh]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    const requestGeneration = generation.current;
    setLoadingMore(true);
    setHistoryError('');
    try {
      const page = await organizationApi.versions(companyId, nextCursor);
      if (requestGeneration !== generation.current) return;
      setVersions((previous) => {
        const seen = new Set(previous.map((version) => version.id));
        return [...previous, ...page.versions.filter((version) => !seen.has(version.id))];
      });
      setNextCursor(page.nextCursor);
    } catch (failure) {
      if (requestGeneration === generation.current)
        setHistoryError(
          failure instanceof Error ? failure.message : 'More history could not be loaded.'
        );
    } finally {
      if (companyScope.current === companyId) setLoadingMore(false);
    }
  }, [companyId, nextCursor, loadingMore]);

  const applyResult = useCallback(
    (result: StructureApplyResult) => {
      setWorkspace((current) =>
        current && current.revision > result.revision
          ? current
          : { snapshot: result.snapshot, revision: result.revision, versionId: result.versionId }
      );
      void refresh();
    },
    [refresh]
  );

  const mutate = useCallback(
    async (
      path: string,
      method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
      data: Record<string, unknown> = {}
    ) => {
      if (!workspace) throw new Error('Wait for the organization to load.');
      const result = await organizationApi.mutate(path, method, data, workspace.revision);
      await refresh();
      return result;
    },
    [workspace, refresh]
  );

  return {
    workspace,
    versions,
    loading,
    error,
    historyError,
    refresh,
    applyResult,
    mutate,
    loadMore,
    hasMore: !!nextCursor,
    loadingMore,
  };
}
